import { useEffect, useState, useRef } from 'react'
import { Button } from '@/components/ui/Button'
import { KanbanBoard } from '@/components/kanban/KanbanBoard'
import { useKanbanStore, STAGE_CONFIG } from '@/stores/kanbanStore'
import { useUIStore } from '@/stores/uiStore'
import { useOpportunities, useMoveOpportunityStage, useCreateOpportunity } from '@/hooks/useOpportunities'
import { useLeads, useCompanyContacts, useCreateContact } from '@/hooks/useLeads'
import { useCatalogItems } from '@/hooks/useCatalog'
import { useUsers } from '@/hooks/useUsers'
import { useAuthStore } from '@/stores/authStore'
import { Input, SearchInput } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { formatCurrency } from '@/lib/utils'
import type { Opportunity, OpportunityCreate, OpportunityStage, Lead, ContactCreate, ContactRole } from '@/types'
import { CONTACT_ROLE_LABELS } from '@/types'
import { Plus, RefreshCw, Loader2, X, Building2, UserPlus, Lightbulb } from 'lucide-react'

function getApiErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as { response?: { data?: { detail?: string }, status?: number } }
  if (axiosErr?.response?.data?.detail) return axiosErr.response.data.detail
  return fallback
}

const ACTIVE_STAGES: OpportunityStage[] = ['qualified', 'proposal_sent', 'negotiation', 'closing']

export function Pipeline() {
  const { setOpportunities, columns } = useKanbanStore()
  const { data, isLoading, refetch } = useOpportunities({ perPage: 100 })
  const { data: usersData } = useUsers({ perPage: 100, isActive: true })
  const users = usersData?.items || []
  const moveStage = useMoveOpportunityStage()
  const [showForm, setShowForm] = useState(false)
  const [initialStage, setInitialStage] = useState<OpportunityStage>('qualified')

  // Filtro de responsável persistido em sessionStorage. Volta da view detalhada
  // mantém o filtro; reseta no próximo browser session.
  const PIPELINE_SS_KEY = 'pipeline:filters'
  const [filterOwner, setFilterOwner] = useState<string>(() => {
    try { return JSON.parse(sessionStorage.getItem(PIPELINE_SS_KEY) || '{}').filterOwner || '' } catch { return '' }
  })
  useEffect(() => {
    try { sessionStorage.setItem(PIPELINE_SS_KEY, JSON.stringify({ filterOwner })) } catch {}
  }, [filterOwner])

  // Listen to uiStore modal for column "+" buttons
  const activeModal = useUIStore((s) => s.activeModal)
  const modalData = useUIStore((s) => s.modalData)
  const closeModal = useUIStore((s) => s.closeModal)

  // Load opportunities into kanban store (filtra por responsável)
  useEffect(() => {
    if (data?.items) {
      const items = filterOwner ? data.items.filter(o => o.owner_id === filterOwner) : data.items
      setOpportunities(items)
    }
  }, [data, setOpportunities, filterOwner])

  // Handle column "+" button (opens create form via uiStore)
  useEffect(() => {
    if (activeModal === 'create-opportunity') {
      const stage = (modalData?.stage as OpportunityStage) || 'qualified'
      setInitialStage(stage)
      setShowForm(true)
      closeModal()
    }
  }, [activeModal, modalData, closeModal])

  // Override moveOpportunity ONCE to add API call
  // Use ref to keep latest mutation without causing re-renders
  const moveStageRef = useRef(moveStage)
  moveStageRef.current = moveStage

  useEffect(() => {
    const originalMove = useKanbanStore.getState().moveOpportunity

    const apiAwareMove = async (oppId: string, fromStage: OpportunityStage, toStage: OpportunityStage) => {
      // Optimistic update in store
      originalMove(oppId, fromStage, toStage)
      try {
        await moveStageRef.current.mutateAsync({ oppId, stage: toStage })
      } catch {
        // Revert on failure
        originalMove(oppId, toStage, fromStage)
      }
    }

    useKanbanStore.setState({ moveOpportunity: apiAwareMove as typeof originalMove })
    return () => {
      useKanbanStore.setState({ moveOpportunity: originalMove })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty deps — run only once on mount

  const totalValue = columns.reduce((s, c) => s + c.totalValue, 0)
  const totalCount = columns.reduce((s, c) => s + c.count, 0)

  return (
    <>
      
      <div className="px-4 py-3 flex flex-col h-[calc(100vh-4rem)]">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="text-sm text-muted-foreground">
              <span className="font-bold text-foreground">{totalCount}</span> oportunidades
              {' | '}
              <span className="font-bold text-foreground">{formatCurrency(totalValue)}</span> total
            </div>
            <select
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              value={filterOwner}
              onChange={(e) => setFilterOwner(e.target.value)}
              title="Filtrar por responsável"
            >
              <option value="">Todos os responsáveis</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />Atualizar
            </Button>
            <Button size="sm" onClick={() => { setInitialStage('qualified'); setShowForm(true) }}>
              <Plus className="h-4 w-4 mr-2" />Nova Oportunidade
            </Button>
          </div>
        </div>

        {/* Kanban */}
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : (
          <div className="flex-1 overflow-hidden">
            <KanbanBoard />
          </div>
        )}
      </div>

      {/* Quick Create Form */}
      {showForm && <PipelineCreateModal stage={initialStage} onClose={() => setShowForm(false)} onCreated={() => refetch()} />}
    </>
  )
}

// ==========================================
// Pipeline Quick Create Modal
// ==========================================

function PipelineCreateModal({ stage, onClose, onCreated }: { stage: OpportunityStage; onClose: () => void; onCreated: () => void }) {
  const createMutation = useCreateOpportunity()
  const currentUser = useAuthStore((s) => s.user)
  const canChangeOwner = (currentUser?.role?.hierarchy_level ?? 0) >= 70

  const [form, setForm] = useState({
    name: '',
    lead_id: '',
    contact_id: '',
    value: '',
    probability: '50',
    expected_close_date: '',
    owner_id: currentUser?.id || '',
    stage: stage,
  })
  const [error, setError] = useState('')
  const [leadSearch, setLeadSearch] = useState('')
  const [showLeadDropdown, setShowLeadDropdown] = useState(false)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [showContactForm, setShowContactForm] = useState(false)

  const { data: leadsData } = useLeads({ search: leadSearch, perPage: 10 })
  const leads = leadsData?.items || []
  const { data: contacts } = useCompanyContacts(selectedLead?.company_id || null)
  const { data: usersData } = useUsers({ perPage: 100, isActive: true })
  const users = usersData?.items || []
  const createContactMutation = useCreateContact()

  const selectClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"

  const handleSelectLead = (lead: Lead) => {
    setSelectedLead(lead)
    setForm({ ...form, lead_id: lead.id, contact_id: lead.primary_contact_id || '' })
    if (!form.name && lead.company?.name) {
      setForm(prev => ({ ...prev, lead_id: lead.id, contact_id: lead.primary_contact_id || '', name: lead.company?.name || '' }))
    }
    setShowLeadDropdown(false)
    setLeadSearch('')
  }

  const [contactForm, setContactForm] = useState<ContactCreate>({
    company_id: '', first_name: '', last_name: '', email: '', phone: '', job_title: '', role: null, is_primary: false,
  })

  const handleCreateContact = async () => {
    if (!selectedLead?.company_id) return
    try {
      const newContact = await createContactMutation.mutateAsync({ ...contactForm, company_id: selectedLead.company_id })
      setForm({ ...form, contact_id: newContact.id })
      setShowContactForm(false)
    } catch (err: unknown) { setError(getApiErrorMessage(err, 'Erro ao criar contato')) }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.lead_id) { setError('Selecione um Lead'); return }
    if (!form.name) { setError('Informe o nome'); return }
    if (!form.value || parseFloat(form.value) <= 0) { setError('Informe o valor'); return }

    try {
      await createMutation.mutateAsync({
        lead_id: form.lead_id,
        name: form.name,
        value: parseFloat(form.value),
        probability: parseInt(form.probability) || 50,
        expected_close_date: form.expected_close_date || null,
        owner_id: form.owner_id || null,
        contact_id: form.contact_id || null,
        stage: form.stage,
      })
      onCreated()
      onClose()
    } catch (err: unknown) { setError(getApiErrorMessage(err, 'Erro ao criar oportunidade')) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-lg shadow-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Nova Oportunidade</h2>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && <div className="p-3 rounded bg-destructive/10 text-destructive text-sm">{error}</div>}

          {/* Lead */}
          <div>
            <label className="text-sm font-medium">Lead *</label>
            {form.lead_id && selectedLead ? (
              <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1 font-medium">{selectedLead.company?.name}</span>
                <Button type="button" variant="ghost" size="icon" onClick={() => { setSelectedLead(null); setForm({ ...form, lead_id: '', contact_id: '' }) }}><X className="h-4 w-4" /></Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Input placeholder="Buscar lead..." value={leadSearch} onChange={(e) => { setLeadSearch(e.target.value); setShowLeadDropdown(true) }} onFocus={() => setShowLeadDropdown(true)} />
                  {showLeadDropdown && leads.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-card border rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {leads.map((lead) => (
                        <button key={lead.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50" onClick={() => handleSelectLead(lead)}>
                          <Building2 className="h-3 w-3 inline mr-1 text-muted-foreground" />{lead.company?.name || 'Sem empresa'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button type="button" variant="outline" onClick={() => window.open('/leads', '_blank')}><Plus className="h-4 w-4" /></Button>
              </div>
            )}
          </div>

          {/* Contact */}
          <div>
            <label className="text-sm font-medium">Contato</label>
            <div className="flex gap-2">
              <select className={selectClass} value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })}>
                <option value="">Selecione</option>
                {contacts?.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''} {c.is_primary ? '★' : ''}</option>)}
              </select>
              {selectedLead && <Button type="button" variant="outline" onClick={() => setShowContactForm(!showContactForm)}><UserPlus className="h-4 w-4" /></Button>}
            </div>
          </div>

          {showContactForm && (
            <div className="p-3 rounded-lg border bg-muted/10 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Nome *" value={contactForm.first_name} onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })} />
                <Input placeholder="Email" value={contactForm.email || ''} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={handleCreateContact} isLoading={createContactMutation.isPending}>Salvar</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowContactForm(false)}>Cancelar</Button>
              </div>
            </div>
          )}

          {/* Name + Value + Prob */}
          <Input placeholder="Nome da oportunidade *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium">Valor (R$) *</label><Input type="number" step="0.01" min="0" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
            <div><label className="text-sm font-medium">Probabilidade</label>
              <select className={selectClass} value={form.probability} onChange={(e) => setForm({ ...form, probability: e.target.value })}>
                <option value="10">10%</option><option value="25">25%</option><option value="50">50%</option><option value="75">75%</option><option value="90">90%</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium">Data Prevista</label><Input type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} /></div>
            <div><label className="text-sm font-medium">Responsavel</label>
              {canChangeOwner ? (
                <select className={selectClass} value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })}>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                </select>
              ) : (
                <div className="flex h-10 items-center px-3 rounded-md border bg-muted/50 text-sm">{currentUser?.full_name || '-'}</div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" isLoading={createMutation.isPending}>Criar Oportunidade</Button>
          </div>
        </form>
      </div>
    </div>
  )
}
